export function formatValue(value, unit) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const num = Number(value);
  if (isNaN(num)) {
    return '—';
  }

  switch (unit) {
    case 'currency':
      return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'decimal':
      return num.toFixed(2);
    case 'months': {
      const totalMonths = Math.round(num);
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;
      if (years === 0) {
        return `${months} mo`;
      }
      if (months === 0) {
        return `${years} yr`;
      }
      return `${years} yr ${months} mo`;
    }
    case 'days':
      return `${num.toFixed(1)} days`;
    case 'percent':
      return `${(Math.round(num * 10) / 10).toFixed(1)}%`;
    default:
      return String(num);
  }
}

export function getDeltaColorClass(raw, polarity) {
  if (raw == null || raw === 0) {
    return 'delta-neutral';
  }
  const isPositiveDelta = raw > 0;
  const isGood = polarity === 'higher_is_better' ? isPositiveDelta : !isPositiveDelta;
  return isGood ? 'delta-positive' : 'delta-neutral';
}

export function formatDelta(valueA, valueB, unit, deltaFormat) {
  if (valueA == null || valueB == null) {
    return '—';
  }

  const raw = valueA - valueB;

  switch (deltaFormat) {
    case 'percentage_points': {
      const sign = raw >= 0 ? '+' : '';
      return `${sign}${(Math.round(raw * 10) / 10).toFixed(1)}%`;
    }
    case 'absolute': {
      if (unit === 'months') {
        const sign = raw >= 0 ? '+' : '-';
        const absRaw = Math.round(Math.abs(raw));
        const years = Math.floor(absRaw / 12);
        const months = absRaw % 12;
        let formatted;
        if (years === 0) {
          formatted = `${months} mo`;
        } else if (months === 0) {
          formatted = `${years} yr`;
        } else {
          formatted = `${years} yr ${months} mo`;
        }
        return `${sign}${formatted}`;
      }
      const sign = raw >= 0 ? '+' : '';
      if (unit === 'days') {
        return `${sign}${raw.toFixed(1)} days`;
      }
      return `${sign}${raw.toFixed(1)}`;
    }
    case 'multiplier': {
      if (valueB === 0) {
        return '—';
      }
      return `${(valueA / valueB).toFixed(1)}×`;
    }
    case 'currency_with_multiplier': {
      const sign = raw >= 0 ? '+' : '-';
      const absDiff = `${sign}£${Math.abs(raw).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      if (valueB === 0) {
        return absDiff;
      }
      const mult = `(${(valueA / valueB).toFixed(1)}×)`;
      return `${absDiff} ${mult}`;
    }
    default:
      return `${raw >= 0 ? '+' : ''}${raw.toFixed(1)}`;
  }
}

export function calculateDelta(valueA, valueB, { deltaFormat, polarity, unit }) {
  if (valueA == null || valueB == null) {
    return { raw: null, formatted: '—', colorClass: 'delta-neutral' };
  }

  const raw = valueA - valueB;
  const formatted = formatDelta(valueA, valueB, unit, deltaFormat);
  // Round to 1dp before determining color so it matches the displayed value
  const rounded = Math.round(raw * 10) / 10;
  const colorClass = getDeltaColorClass(rounded, polarity);

  return { raw, formatted, colorClass };
}

export function buildMetricsMap(metrics) {
  const map = {};
  for (const m of metrics) {
    map[m.id] = m;
  }
  return map;
}

// Treats values that round to 0.0 as empty so we don't render bars that display as "0.0%".
export function hasMeaningfulValue(value) {
  return value != null && Number.isFinite(Number(value)) && Math.round(Number(value) * 10) !== 0;
}
